import React from 'react';
import { motion } from 'framer-motion';
import {
    ShoppingCart, CreditCard, Box, Truck, BarChart,
    ShieldCheck, ArrowRight, Zap, ChevronLeft, Store,
    Users, RefreshCw, Smartphone, Heart
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO_Component from '../../components/SEO';

const ECommerce = () => {
    const features = [
        { title: "Storefront Design", desc: "Conversion-optimized shopping experiences.", icon: Store },
        { title: "Payment Systems", desc: "Secure multi-gateway payment integrations.", icon: CreditCard },
        { title: "Inventory Sync", desc: "Real-time stock management across channels.", icon: Box },
        { title: "Mobile Shopping", desc: "PWA and native mobile commerce solutions.", icon: Smartphone },
        { title: "User Analytics", desc: "Deep insights into customer buying behavior.", icon: BarChart },
        { title: "Order Flow", desc: "Automated fulfillment and shipping logic.", icon: Truck }
    ];

    return (
        <div className="bg-[#02000c] min-h-screen text-white pt-20 overflow-hidden">
            <SEO_Component
                title="E-commerce Solutions | Scale Your Online Store - MehareTech"
                description="Transform your retail business with MehareTech's high-performance e-commerce development. Secure payments, real-time inventory, and conversion-optimized storefronts."
                keywords="ecommerce development, online store builder, payment gateway integration, secure shopping cart, retail automation, inventory management software, e-commerce agency India"
            />

            {/* Back to Services Link */}
            <div className="container mx-auto px-6 mb-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-orange-500 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors group">
                    <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Services
                </Link>
            </div>

            {/* Hero Section */}
            <section className="relative py-12 px-6">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-orange-600/10 blur-[120px] -z-10"></div>

                <div className="container mx-auto text-center max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-orange-500 font-black uppercase tracking-[0.3em] text-[10px] mb-2 block">
                            Digital Commerce
                        </span>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tighter">
                            SELL BEYOND <span className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 bg-clip-text text-transparent">BOUNDARIES</span>
                        </h1>
                        <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto font-medium">
                            We build high-performance online stores that turn visitors into loyal customers.
                        </p>
                        <button className="px-8 py-3 bg-orange-600 text-white font-bold rounded-xl hover:bg-orange-700 transition-all flex items-center gap-2 mx-auto text-xs">
                            Launch Your Store <ArrowRight size={18} />
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Grid */}
            <section className="py-12 px-6 bg-slate-900/40 border-y border-white/5">
                <div className="container mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        {features.map((item, i) => (
                            <motion.div
                                key={i}
                                className="p-6 bg-slate-900 border border-white/5 rounded-2xl hover:border-orange-500/30 transition-all group"
                            >
                                <item.icon size={26} className="text-orange-500 mb-4 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-bold mb-1">{item.title}</h3>
                                <p className="text-slate-400 text-[11px] leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Info Section */}
            <section className="py-12 px-6">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="md:w-1/2 text-left">
                            <h2 className="text-3xl font-black mb-6 tracking-tight">
                                Conversion <span className="text-orange-500">First</span>
                            </h2>
                            <div className="space-y-3 font-medium">
                                {[
                                    { label: "1-Click Checkout", icon: Zap },
                                    { label: "Secure Transactions", icon: ShieldCheck },
                                    { label: "Customer Loyalty", icon: Heart }
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                                        <div className="w-8 h-8 rounded-lg bg-orange-600/10 flex items-center justify-center">
                                            <item.icon size={16} className="text-orange-500" />
                                        </div>
                                        {item.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="md:w-1/2 relative group">
                            <img
                                src="https://images.pexels.com/photos/34577/pexels-photo.jpg"
                                alt="E-commerce"
                                className="rounded-3xl shadow-xl w-full h-48 object-cover border border-white/10"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ECommerce;
